import {
  APP_LOCALES,
  nextAppLocale,
  normalizeAppLocale,
  type AppLocale,
} from "./locale";

export { MINI_APP_ASSET_ORIGIN, publicAssetUrl } from "./catalog-asset-url";
const DYNAMIC_ROUTE_LOCALE_PATTERN = APP_LOCALES
  .map((locale) => locale.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  .join("|");
const DYNAMIC_ROUTE_PATTERN = new RegExp(
  `^/(${DYNAMIC_ROUTE_LOCALE_PATTERN})/(list|product)/([a-f\\d]{36})/?$`,
  "i",
);

export interface WebsiteRouteContext {
  locale?: AppLocale;
  store?: string;
  categoryId?: string;
  catalogId?: string;
  productId?: string;
  productGroupId?: string;
  productGroupKeys?: string[];
  sharedListToken?: string;
  sharedProductToken?: string;
  alternatePath?: string;
}

function normalizedContext(value: unknown): WebsiteRouteContext | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const locale = normalizeAppLocale(candidate.locale) ?? undefined;
  const stringValue = (key: string, maximum: number): string | undefined => {
    const item = candidate[key];
    return typeof item === "string" && item.length > 0 && item.length <= maximum
      ? item
      : undefined;
  };
  const context: WebsiteRouteContext = {
    ...(locale ? { locale } : {}),
    ...(stringValue("store", 80) ? { store: stringValue("store", 80) } : {}),
    ...(stringValue("categoryId", 128)
      ? { categoryId: stringValue("categoryId", 128) }
      : {}),
    ...(stringValue("catalogId", 128)
      ? { catalogId: stringValue("catalogId", 128) }
      : {}),
    ...(stringValue("productId", 128)
      ? { productId: stringValue("productId", 128) }
      : {}),
    ...(stringValue("productGroupId", 64)
      ? { productGroupId: stringValue("productGroupId", 64) }
      : {}),
    ...(Array.isArray(candidate.productGroupKeys) && candidate.productGroupKeys.length <= 20000
      && candidate.productGroupKeys.every(key => typeof key === "string" && key.length <= 200 && /^[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+$/.test(key))
      ? { productGroupKeys: candidate.productGroupKeys as string[] } : {}),
    ...(stringValue("sharedListToken", 36)
      ? { sharedListToken: stringValue("sharedListToken", 36) }
      : {}),
    ...(stringValue("sharedProductToken", 36)
      ? { sharedProductToken: stringValue("sharedProductToken", 36) }
      : {}),
    ...(stringValue("alternatePath", 1_000)
      ? { alternatePath: stringValue("alternatePath", 1_000) }
      : {}),
  };
  return Object.keys(context).length > 0 ? context : null;
}

export function parseDynamicWebsiteRoute(
  pathname: string,
): WebsiteRouteContext | null {
  const match = DYNAMIC_ROUTE_PATTERN.exec(pathname);
  if (!match) {
    return null;
  }
  const locale = normalizeAppLocale(match[1]!.toLowerCase());
  if (!locale) {
    return null;
  }
  const kind = match[2]!.toLowerCase();
  const token = match[3]!.toLowerCase();
  const otherLocale = nextAppLocale(locale);
  return {
    locale,
    ...(kind === "list"
      ? { sharedListToken: token }
      : { sharedProductToken: token }),
    alternatePath: `/${otherLocale}/${kind}/${token}/`,
  };
}

export function readWebsiteRouteContext(
  documentValue: Document = document,
  pathname = window.location.pathname,
): WebsiteRouteContext | null {
  const element = documentValue.querySelector<HTMLScriptElement>(
    "#kiloprice-route-context",
  );
  if (element?.textContent) {
    try {
      const parsed = normalizedContext(JSON.parse(element.textContent));
      if (parsed) {
        return parsed;
      }
    } catch {
      // A malformed optional route hint must not block the main product feed.
    }
  }
  return parseDynamicWebsiteRoute(pathname);
}

export function websiteHomeUrl(locale: AppLocale): string {
  return `${window.location.origin}/${locale}/`;
}

/** A cached landing must not keep promoting an offer after Moldova midnight. */
export function expireWebsiteOffers(today: string, endedLabel: string): void {
  document.querySelectorAll<HTMLElement>("[data-website-valid-until]").forEach(element => {
    if ((element.dataset.websiteValidUntil ?? today) < today) element.hidden = true;
  });
  const intro = document.querySelector<HTMLElement>("[data-website-offer-until]");
  if (!intro?.dataset.websiteOfferUntil || intro.dataset.websiteOfferUntil >= today) return;
  const description = intro.querySelector("p");
  if (description) description.textContent = endedLabel;
  document.querySelectorAll<HTMLElement>(".website-product-detail, .website-offer-directory").forEach(element => { element.hidden = true; });
  document.querySelector<HTMLMetaElement>('meta[name="robots"]')?.setAttribute("content", "noindex, follow");
  document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]').forEach(element => {
    try {
      const data = JSON.parse(element.textContent ?? "null");
      for (const node of Array.isArray(data) ? data : [data]) {
        if (node?.["@type"] === "Product") delete node.offers;
      }
      element.textContent = JSON.stringify(data).replaceAll("<", "\\u003c");
    } catch { element.remove(); }
  });
}

export function websiteLocalizedUrl(
  locale: AppLocale,
  locationValue: Pick<Location, "origin" | "pathname" | "search" | "hash"> =
    window.location,
): string {
  // Slugs and route segments differ by language. Changing just /ru/ to /ro/
  // would produce a non-existent URL for stores, categories and products.
  if (typeof document !== "undefined") {
    const alternate = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${locale}"]`);
    if (alternate) {
      const url = new URL(alternate.href);
      return `${locationValue.origin}${url.pathname}${locationValue.search}${locationValue.hash}`;
    }
  }
  const segments = locationValue.pathname.split("/");
  if (normalizeAppLocale(segments[1]?.toLowerCase())) {
    segments[1] = locale;
  } else {
    segments.splice(1, 0, locale);
  }
  const pathname = segments.join("/").replace(/\/{2,}/g, "/");
  return `${locationValue.origin}${pathname}${locationValue.search}${locationValue.hash}`;
}
